import React, { useEffect, useMemo } from 'react'
import { Button, Form } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useStore } from '../../store/store'
import { useOverlay, leftShownStatus } from '../../contexts/OverlayContext'
import { navbarHeight, sidebarWidth } from '../../constants/gridConfig'
import { PANEL_LIGHT_BG, PRIMARY_DARK } from '../../constants/constants'
import { getVariablesGroupedByContainer } from '../../utils/variables'
import { isAnswersVariableBox, selectionKeysForBox, simulateVariableTextBox } from '../../utils/boxes'
import { useVariableBoxForm } from '../VariableHandler/FormCreator/useVariableBoxForm'
import { renderSpecificBoxEditor } from '../VariableHandler/FormCreator/FormBoxEditor'
import SelectionMiddle from '../VariableHandler/SelectionMiddle'
import FormBoxRemark from '../VariableHandler/FormCreator/FormBoxRemark'

const DRAWER_WIDTH = 450

// Contenu isolé pour bénéficier d'un remontage par variable (key=box.id dans
// le parent) : chaque variable a son propre form (initialValues lues du box).
// formPart 'right' : aperçu "Valeur par défaut" + tableau "Valeurs
// conditionnelles" (déclencheurs) — la moitié du formulaire jamais visible au
// clic sur la variable (le panel de droite montre déjà les champs de base).
const DrawerContent = ({ box, variablesGroupedByContainer }) => {
  const { form, onBlur, validateFormAndRetrieveValues } = useVariableBoxForm(box.id)
  const { arrayKey, selectionKey } = selectionKeysForBox(box)

  return (
    <Form
      form={form}
      colon={false}
      className='variable-editor__form'
      layout='horizontal'
      validateMessages={{ required: 'Champ obligatoire.' }}
    >
      {/* Case à cocher / choix : la section du panel de droite ne garde que le
          nom et la liste des réponses, faute de largeur. Le reste des réglages
          de formulaire est ici, avant la valeur par défaut et les déclencheurs
          (le style, lui, a sa propre section d'inspecteur). */}
      {isAnswersVariableBox(box.type) && (
        <div className='variable-editor__middle'>
          <SelectionMiddle
            box={box}
            onBlur={onBlur}
            form={form}
            arrayKey={arrayKey}
            selectionKey={selectionKey}
          />
          <FormBoxRemark box={box} onBlur={onBlur} form={form} unselectBoxOnFocus={false} />
        </div>
      )}
      {renderSpecificBoxEditor(box, {
        form,
        onBlur,
        validateFormAndRetrieveValues,
        variablesGroupedByContainer,
        formPart: 'right',
        setCreateItemType: () => {},
      })}
    </Form>
  )
}

// Panneau coulissant d'édition rapide, ancré au bord gauche du panel de droite
// (right: sidebarWidth) et glissant par-dessus le document, sans masque : rail
// gauche, document et panel de droite restent visibles. Réutilise les mêmes
// éditeurs (formPart 'left') que le formulaire plein écran. Pour l'édition
// avancée (déclencheurs / valeurs conditionnelles), le bouton de pied bascule
// vers le formulaire plein écran.
// `boxId` : la box à éditer. Par défaut la variable sélectionnée dans le
// formulaire (boxVariableSelectedId) ; le panel de droite passe l'id de la box
// sélectionnée au document pour les cases à cocher et les choix, dont la
// sélection ne passe pas par le formulaire.
const VariableDrawer = ({ open, onClose, boxId }) => {
  // Les barres flottantes des blocs passeraient par-dessus le volet (index.less).
  useEffect(() => {
    document.body.classList.toggle('variable-drawer-open', !!open)
    return () => document.body.classList.remove('variable-drawer-open')
  }, [open])

  const { updateLeftShownStatus } = useOverlay()
  const boxVariableSelectedId = useStore(({ boxVariableSelectedId }) => boxVariableSelectedId)
  const targetBoxId = boxId ?? boxVariableSelectedId
  const boxes = useStore(({ boxes }) => boxes)
  const variables = useStore(({ variables }) => variables)
  const variableGroups = useStore(({ variableGroups }) => variableGroups)
  const containers = useStore(({ containers }) => containers)
  const columns = useStore(({ columns }) => columns)
const rowContainers = useStore(({ rowContainers }) => rowContainers)
  const masks = useStore(({ masks }) => masks)

  const box = useMemo(() => {
    const found = boxes.find((b) => b.id === targetBoxId)
    if (found) return found
    if (targetBoxId && variables[targetBoxId]) {
      return simulateVariableTextBox([targetBoxId, variables[targetBoxId]])
    }
    return null
  }, [targetBoxId, boxes, variables])

  // Calcul coûteux : uniquement quand le drawer est ouvert.
  const variablesGroupedByContainer = useMemo(() => {
    if (!open) return []
    return getVariablesGroupedByContainer({
      variableGroups,
      variables,
      boxes: [...boxes, ...masks.map((m) => m.boxes).flat()],
      containers,
      columns,
      rowContainers,
    })
  }, [open, variableGroups, variables, boxes, masks, containers, columns, rowContainers])

  const answersCount = isAnswersVariableBox(box?.type)
    ? box[selectionKeysForBox(box).arrayKey]?.length ?? 0
    : 0

  // Plus de variable sélectionnée (désélection au canvas) → fermer.
  useEffect(() => {
    if (!targetBoxId) onClose()
  }, [targetBoxId, onClose])

  // Box supprimée (corbeille de la section Actions) alors que le drawer est
  // ouvert : plus rien à éditer, le drawer resterait ouvert et vide.
  useEffect(() => {
    if (open && !box) onClose()
  }, [open, box, onClose])

  return (
    <div
      // Des champs portent data-role=pageBackground (les nombres min/max) : le
      // clic remontait au onClick de Layout, qui désélectionne la box — ce qui
      // ferme le drawer ouvert pour une case à cocher (cf. RightSide).
      onClick={(event) => event.stopPropagation()}
      style={{
        position: 'fixed',
        top: navbarHeight,
        right: sidebarWidth,
        height: `calc(100vh - ${navbarHeight}px)`,
        width: DRAWER_WIDTH,
        backgroundColor: PANEL_LIGHT_BG,
        borderLeft: '1px solid rgba(11, 34, 57, 0.25)',
        boxShadow: '-8px 0 24px rgba(11, 34, 57, 0.12)',
        zIndex: 14,
        // Fermé : décaler d'au moins (largeur du drawer + son ancrage right)
        // pour le sortir entièrement de l'écran. Un simple `DRAWER_WIDTH` ne
        // suffit pas car le drawer est ancré à `right: sidebarWidth` → il
        // resterait `sidebarWidth` px visibles à droite.
        transform: open ? 'translateX(0)' : `translateX(${DRAWER_WIDTH + sidebarWidth + 40}px)`,
        transition: 'transform 0.25s ease',
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid rgba(11, 34, 57, 0.1)',
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 700, color: PRIMARY_DARK }}>Édition rapide</span>
        <FontAwesomeIcon
          icon={icon({ name: 'chevron-right', style: 'solid' })}
          onClick={onClose}
          style={{ cursor: 'pointer', color: PRIMARY_DARK }}
        />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {open && box && (
          // La clé porte aussi le nombre de réponses : les nombres min/max sont
          // bornés par ce nombre et lus une fois au montage (initialValue).
          // Ajouter ou retirer une réponse depuis le panel de droite pendant que
          // le drawer est ouvert laissait donc des bornes périmées, réécrites
          // telles quelles à la sortie du champ.
          <DrawerContent
            key={`${box.id}-${answersCount}`}
            box={box}
            variablesGroupedByContainer={variablesGroupedByContainer}
          />
        )}
      </div>

      <div style={{ padding: 16, borderTop: '1px solid rgba(11, 34, 57, 0.1)', flexShrink: 0 }}>
        <Button
          block
          ghost
          style={{ color: PRIMARY_DARK, borderColor: PRIMARY_DARK }}
          icon={<FontAwesomeIcon icon={icon({ name: 'arrow-up-right-from-square', style: 'solid' })} style={{ marginRight: 8 }} />}
          onClick={() => {
            // Avec l'id : le formulaire s'ouvre sur la box et y scrolle. Une
            // case à cocher sélectionnée au document n'est pas passée par
            // boxVariableSelectedId, sans quoi le formulaire s'ouvrirait vide.
            updateLeftShownStatus(leftShownStatus.VARIABLES, targetBoxId)
            onClose()
          }}
        >
          Ouvrir dans le formulaire
        </Button>
      </div>
    </div>
  )
}

export default VariableDrawer
